import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from '@/contexts/AuthContext';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import type { UpdateProfileRequest } from '@/types/auth';
import { styled } from '@mui/material/styles';
import { Button, Typography, Input } from '@/components/ui';
import { authService } from '@/services/authService';

// Styled components for profile update
const ProfileContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  margin: '0 auto',
  padding: theme.spacing(4),
  backgroundColor: 'white',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
}));

const ProfileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
}));


const FormSection = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'flex-end',
  marginTop: theme.spacing(3),
}));

// Validation schema for profile update
const profileSchema = yup.object({
  name: yup
    .string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters'),
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  phone: yup
    .string()
    .matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit phone number')
    .nullable(),
});

// Build initial values purely from context user
const buildInitialValues = (user: any): UpdateProfileRequest => ({
  name: user?.name || '',
  email: user?.email || '',
  phone: user?.phone || '',
});

export const UpdateProfileForm: React.FC = () => {

  const navigate = useNavigate();
  const { user: contextUser, updateProfile, loading, getProfile } = useAuth();
  // Add local loading state for profile fetch

  // Local user state for fetched profile
  const [user, setUser] = useState<any>(contextUser);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await authService.getProfile();
        setUser(response.data); // Set fetched user to local state

      } catch (error) {
        console.error('Failed to fetch profile:', error);
        navigate('/auth/login');
      }
    };

    fetchData();
  }, []); // Em


  // Ensure latest profile on mount (optional, if you want to sync with context)
  useEffect(() => {
    if (!contextUser) {
      getProfile().catch(() => { });
    }
  }, [contextUser, getProfile]);

  // Use local user state for Formik initial values
  const initialValues = useMemo(() => buildInitialValues(user), [user]);


  // Handle form submission
  const handleSubmit = async (
    values: UpdateProfileRequest,
    { setFieldError, setSubmitting }: {
      setFieldError: (field: string, message: string) => void;
      setSubmitting: (isSubmitting: boolean) => void;
    }
  ) => {
    try {
      await updateProfile(values);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
      setFieldError('name', errorMessage);
    } finally {
      setSubmitting(false);
    }
  };


  if (!user) {
    return null; // Don't render if user is not available
  }

  return (
    <ProfileContainer>
      {/* Profile Header */}
      <ProfileHeader>
        <Typography
          variant="h4"
          weight="bold"
          color="textPrimary"
          sx={{ marginBottom: 1 }}
        >
          Update Profile
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Update your personal information
        </Typography>
      </ProfileHeader>

      {/* Profile Form */}
      <Formik
        initialValues={initialValues}
        validationSchema={profileSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue, setFieldTouched, isSubmitting }) => (
          <Form>
            <FormSection>
              {/* Name Field */}
              <Box>
                <Input
                  fullWidth
                  label="Full Name"
                  name="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={values.name || ''}
                  onChange={e => setFieldValue('name', e.target.value)}
                  onBlur={() => setFieldTouched('name', true)}
                  error={touched.name && !!errors.name}
                  helperText={touched.name && errors.name}
                  disabled={loading || isSubmitting}
                  size="large"
                  variant="outlined"
                />
              </Box>

              {/* Email Field */}
              <Box>
                <Input
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={values.email || ''}
                  onChange={e => setFieldValue('email', e.target.value)}
                  onBlur={() => setFieldTouched('email', true)}
                  error={touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  disabled={loading || isSubmitting}
                  size="large"
                  variant="outlined"
                />
              </Box>

              {/* Phone Field */}
              <Box>
                <Input
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  value={values.phone || ''}
                  onChange={e => setFieldValue('phone', e.target.value)}
                  onBlur={() => setFieldTouched('phone', true)}
                  error={touched.phone && !!errors.phone}
                  helperText={touched.phone && errors.phone}
                  disabled={loading || isSubmitting}
                  size="large"
                  variant="outlined"
                />
              </Box>
            </FormSection>

            {/* Action Buttons */}
            <ButtonGroup>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/dashboard')}
                disabled={loading || isSubmitting}
                sx={{ px: 4 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="large"
                loading={loading || isSubmitting}
                loadingText="Updating profile..."
                disabled={!values.name || !values.email || loading || isSubmitting}
                sx={{ px: 4 }}
              >
                Update Profile
              </Button>
            </ButtonGroup>
          </Form>
        )}
      </Formik>
    </ProfileContainer>
  );
};

export default UpdateProfileForm;